import React, { useCallback, useEffect, useState } from 'react';
import { apiGet, apiPost, apiPatch, apiDelete } from '../api';

// Reel moderation: the switch that stops new uploads, and the queue of reels
// users have reported.
//
// The switch is deliberately narrow — it refuses NEW uploads and leaves
// everything already posted playing. That is what makes it usable in a hurry:
// closing the tap during an abuse wave costs nothing to the people who have
// done nothing wrong, and turning it back on needs no cleanup.
const Reels = () => {
  const [settings, setSettings] = useState(null);
  const [savingSwitch, setSavingSwitch] = useState(false);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      const [s, r] = await Promise.all([
        apiGet('/api/admin/settings'),
        apiGet('/api/admin/reels/reports'),
      ]);
      setSettings(s || {});
      setReports(Array.isArray(r) ? r : []);
      setError(null);
    } catch (e) {
      setError('Failed to load. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleUploads = async () => {
    const next = !(settings?.reelsUploadEnabled !== false);
    if (!next && !window.confirm('Stop all new reel uploads?\n\nExisting reels keep playing. You can turn this back on at any time.')) return;
    setSavingSwitch(true);
    try {
      const res = await apiPatch('/api/admin/settings', { reelsUploadEnabled: next });
      if (res?.success === false) throw new Error(res.message);
      setSettings(res.settings || { ...settings, reelsUploadEnabled: next });
    } catch (e) {
      alert(`Could not change the setting: ${e.message}`);
    } finally {
      setSavingSwitch(false);
    }
  };

  const deleteReel = async (report) => {
    if (!report.reelId) return;
    if (!window.confirm('Remove this reel? It stops appearing in the feed immediately.')) return;
    setBusyId(report.id);
    try {
      const res = await apiDelete(`/api/admin/reels/${report.reelId}`);
      if (res?.success === false) throw new Error(res.message);
      // Every pending report for this reel is closed server-side, so drop them
      // all from the list rather than just the row that was clicked.
      setReports((prev) => prev.filter((r) => r.reelId !== report.reelId));
    } catch (e) {
      alert(`Could not remove the reel: ${e.message}`);
    } finally {
      setBusyId(null);
    }
  };

  // Closes the report and leaves the reel up — for reports that turn out to be
  // baseless. Uses the same status endpoint the Report Request page uses, so
  // both queues agree on what "handled" means.
  const dismiss = async (report) => {
    setBusyId(report.id);
    try {
      const res = await apiPost(`/api/admin/reports/${report.id}/status`, { status: 'rejected' });
      if (res?.success === false) throw new Error(res.message);
      setReports((prev) => prev.filter((r) => r.id !== report.id));
    } catch (e) {
      alert(`Could not dismiss the report: ${e.message}`);
    } finally {
      setBusyId(null);
    }
  };

  const uploadsOn = settings?.reelsUploadEnabled !== false;

  if (loading) return <div className="dark-loading"><div className="spinner" /><p>Loading Reels…</p></div>;
  if (error) return <div className="dark-error">{error}</div>;

  return (
    <div className="dark-page">
      <h2>Reels</h2>

      <div style={styles.switchCard}>
        <div>
          <div style={styles.switchTitle}>Reel uploads</div>
          <div style={styles.switchHint}>
            {uploadsOn
              ? 'Users can post new reels. Turn this off to stop uploads during abuse — reels already posted keep playing.'
              : 'Uploads are stopped. Users see “Reel uploads are turned off at the moment.” Existing reels still play.'}
          </div>
        </div>
        <button
          type="button"
          onClick={toggleUploads}
          disabled={savingSwitch}
          style={{ ...styles.switch, ...(uploadsOn ? styles.switchOn : styles.switchOff) }}
        >
          {savingSwitch ? '…' : uploadsOn ? 'ON' : 'OFF'}
        </button>
      </div>

      <h3 style={styles.sectionTitle}>
        Reported reels {reports.length > 0 && <span style={styles.count}>{reports.length}</span>}
      </h3>

      {reports.length === 0 ? (
        <p className="text-muted">No reported reels. Nothing to review.</p>
      ) : (
        <div className="dark-table-wrap">
          <table className="dark-table">
            <thead>
              <tr>
                <th>Reel</th>
                <th>Posted by</th>
                <th>Reported by</th>
                <th>Reason</th>
                <th className="text-center">Action</th>
              </tr>
            </thead>
            <tbody>
              {reports.map((r) => (
                <tr key={r.id}>
                  <td>
                    {r.reel ? (
                      <div style={styles.reelCell}>
                        {/* Playable in place. Moderation means watching the
                            thing that was reported, and a link to a new tab
                            makes that slow enough that it gets skipped.
                            preload="none" so opening the queue does not pull
                            down every reported video at once. */}
                        <video
                          src={r.reel.videoUrl}
                          poster={r.reel.thumbnailUrl || undefined}
                          style={styles.player}
                          controls
                          preload="none"
                          playsInline
                        />
                        <div>
                          <div style={styles.caption}>
                            {r.reel.caption || <span className="text-muted">no caption</span>}
                          </div>
                          <a href={r.reel.videoUrl} target="_blank" rel="noreferrer" style={styles.link}>
                            Open full size
                          </a>
                          {r.reel.status !== 'active' && <div style={styles.removed}>already removed</div>}
                        </div>
                      </div>
                    ) : (
                      <span className="text-muted">reel deleted</span>
                    )}
                  </td>
                  <td>
                    {r.reported?.username || '—'}
                    <div className="text-muted">ID {r.reported?.ID ?? '—'}</div>
                  </td>
                  <td>
                    {r.reporter?.username || '—'}
                    <div className="text-muted">ID {r.reporter?.ID ?? '—'}</div>
                  </td>
                  <td>
                    <span className="font-medium">{r.reason || '—'}</span>
                    {!!r.details && <div className="text-muted">{r.details}</div>}
                  </td>
                  <td className="text-center">
                    <button
                      className="btn-action"
                      onClick={() => deleteReel(r)}
                      disabled={busyId === r.id || !r.reelId || r.reel?.status !== 'active'}
                    >
                      {busyId === r.id ? 'Working…' : 'Remove reel'}
                    </button>
                    <button
                      className="btn-action"
                      onClick={() => dismiss(r)}
                      disabled={busyId === r.id}
                      style={{ marginLeft: 8, opacity: 0.75 }}
                    >
                      Dismiss
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const styles = {
  switchCard: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 20,
    padding: '16px 18px',
    borderRadius: 12,
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.09)',
    marginBottom: 26,
  },
  switchTitle: { fontSize: 15, fontWeight: 700, marginBottom: 4 },
  switchHint: { fontSize: 12.5, opacity: 0.65, maxWidth: 620, lineHeight: 1.5 },
  switch: {
    minWidth: 78,
    padding: '10px 16px',
    borderRadius: 20,
    border: 'none',
    fontWeight: 800,
    fontSize: 13,
    cursor: 'pointer',
    color: '#fff',
  },
  switchOn: { background: '#1DB954' },
  switchOff: { background: '#E03131' },

  sectionTitle: { fontSize: 15, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 },
  count: {
    background: '#E03131',
    color: '#fff',
    borderRadius: 10,
    padding: '1px 8px',
    fontSize: 12,
    fontWeight: 700,
  },

  reelCell: { display: 'flex', gap: 12, alignItems: 'flex-start' },
  player: {
    width: 132,
    height: 186,
    borderRadius: 8,
    background: '#000',
    objectFit: 'cover',
  },
  link: { color: '#6ea8ff', fontWeight: 600, fontSize: 13 },
  caption: { fontSize: 12, opacity: 0.7, marginTop: 3, maxWidth: 260 },
  removed: { fontSize: 11, color: '#E03131', marginTop: 3, fontWeight: 600 },
};

export default Reels;
